import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/lib/api";
import { helper } from "@/lib/helper";
import { cn } from "@/lib/utils";
import { Loader2, User2, Phone, CreditCard, Users, RefreshCw } from "lucide-react";

type RefereeRow = {
  id?: number | string;
  guarantor_name?: string;
  phone_number?: string;
  national_id?: string;
  RELATIONSHIP?: string;
};

type PaginationMeta = {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  has_more: boolean;
};

export default function CustomerRefereesTab({ customerId }: { customerId: string }) {
  const perPage = 10;

  const [rows, setRows] = useState<RefereeRow[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  function normalize(res: any): { rows: RefereeRow[]; meta: PaginationMeta | null } {
    const payload = res?.data ?? res;

    // apiFetch sometimes returns the array directly
    if (Array.isArray(payload)) return { rows: payload, meta: null };

    const rows =
      (Array.isArray(payload?.data) && payload.data) ||
      (Array.isArray(payload?.data?.data) && payload.data.data) ||
      [];

    const meta = payload?.meta ?? payload?.data?.meta ?? null;

    return { rows, meta };
  }

  async function fetchReferees(p = 1) {
    if (!customerId) return;
    if (loading) return;

    setLoading(true);
    setErr("");

    try {
      const res = await apiFetch<any>(
        `/customer-profile/${customerId}/referees?page=${p}&per_page=${perPage}`
      );

      const out = normalize(res);
      setRows(out.rows);
      setMeta(out.meta);
      setPage(p);
    } catch (e: any) {
      setErr(e?.message || "Failed to load referees");
      setRows([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchReferees(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const canPrev = page > 1 && !loading;
  const canNext = Boolean(meta?.has_more) && !loading;

  const total = useMemo(() => (meta?.total ?? rows.length), [meta, rows.length]);

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm font-semibold">Referees / Guarantors</div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => fetchReferees(1)}
            disabled={loading}
            className="gap-2"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </Button>

          <Button size="sm" onClick={() => alert("TODO: Add Referee")} className="gap-2">
            <Users className="h-4 w-4" />
            Add Referee
          </Button>
        </div>
      </div>

      {/* Error */}
      {err ? (
        <Card>
          <CardContent className="p-4 text-sm text-destructive">{err}</CardContent>
        </Card>
      ) : null}

      {/* Desktop table */}
      <div className="hidden lg:block overflow-x-auto rounded-xl border">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-muted/50">
            <tr className="text-left">
              <th className="p-3">#</th>
              <th className="p-3">Name</th>
              <th className="p-3">Relationship</th>
              <th className="p-3">Phone</th>
              <th className="p-3">National ID</th>
              
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="p-6 text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading referees...
                  </div>
                </td>
              </tr>
            ) : rows.length ? (
              rows.map((r, idx) => (
                <tr key={String(r.id ?? idx)} className="border-t">
                  <td className="p-3">{idx + 1}</td>

                  <td className="p-3 font-semibold">{helper.safe(r.guarantor_name)}</td>

                  <td className="p-3">
                    <span className="inline-flex px-2.5 py-1 rounded-full text-xs border bg-primary/10 text-primary border-primary/20 font-semibold">
                      {helper.safe(r.RELATIONSHIP, "—")}
                    </span>
                  </td>

                  <td className="p-3">{helper.safe(r.phone_number)}</td>
                  <td className="p-3">{helper.safe(r.national_id)}</td>

                  
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="p-6 text-muted-foreground">
                  No referees found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="lg:hidden space-y-3">
        {loading ? (
          <Card className="shadow-card">
            <CardContent className="p-4 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading referees...
            </CardContent>
          </Card>
        ) : rows.length ? (
          rows.map((r, idx) => (
            <Card key={String(r.id ?? idx)} className="shadow-card rounded-2xl border bg-background">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                        <User2 className="h-4 w-4" />
                      </div>

                      <div className="min-w-0">
                        <div className="font-semibold truncate">{helper.safe(r.guarantor_name)}</div>
                        <div className="text-xs text-muted-foreground">
                          Relationship:{" "}
                          <span className="text-foreground font-semibold">
                            {helper.safe(r.RELATIONSHIP, "—")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <MiniMeta icon={Phone} label="Phone" value={helper.safe(r.phone_number)} />
                  <MiniMeta icon={CreditCard} label="National ID" value={helper.safe(r.national_id)} />
                </div>

                <div className="text-[11px] text-muted-foreground">
                  Referee ID: <span className="text-foreground font-semibold">{helper.safe(r.id)}</span>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="text-sm text-muted-foreground">No referees found.</div>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-muted-foreground">
          {meta ? `Page ${meta.current_page} of ${meta.last_page} • Total ${meta.total}` : `Total ${total}`}
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={!canPrev} onClick={() => fetchReferees(page - 1)}>
            Prev
          </Button>
          <Button size="sm" variant="outline" disabled={!canNext} onClick={() => fetchReferees(page + 1)}>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

function MiniMeta({
  icon: Icon,
  label,
  value,
}: {
  icon: any;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-xl border bg-background p-3">
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] text-muted-foreground font-medium">{label}</div>
          <div className="text-sm font-semibold truncate">{value}</div>
        </div>
      </div>
    </div>
  );
}